import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, deleteUser } from '../utils/firebase';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export default function DeleteUser() {
    const { t } = useTranslation();
    const [searchInput, setSearchInput] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [privateKey, setPrivateKey] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [deletionResult, setDeletionResult] = useState(null);

    const handleSearchUser = async () => {
        if (!searchInput.trim()) {
            toast.error('Please enter a user email or UID');
            return;
        }

        try {
            const usersRef = collection(db, 'users');
            
            // Try to search by email first
            let q = query(usersRef, where('email', '==', searchInput.trim()));
            let querySnapshot = await getDocs(q);

            // If not found by email, try by UID
            if (querySnapshot.empty) {
                q = query(usersRef, where('uid', '==', searchInput.trim()));
                querySnapshot = await getDocs(q);
            }

            if (querySnapshot.empty) {
                toast.error('User not found');
                setSelectedUser(null);
                return;
            }

            const userData = querySnapshot.docs[0].data();
            setSelectedUser({
                ...userData,
                id: querySnapshot.docs[0].id,
            });
            toast.success('User found');
        } catch (error) {
            console.error('Error searching user:', error);
            toast.error('Error searching for user');
        }
    };

    const handleOpenConfirmDialog = () => {
        if (!selectedUser) {
            toast.error('Please search and select a user first');
            return;
        }
        if (!privateKey.trim()) {
            toast.error('Please enter the deletion private key');
            return;
        }
        setShowConfirmDialog(true);
        setConfirmText('');
    };

    const handleDeleteUser = async () => {
        if (confirmText !== 'DELETE') {
            toast.error('Please type DELETE to confirm');
            return;
        }

        setIsDeleting(true);
        setDeletionResult(null);

        try {
            const result = await deleteUser({
                userId: selectedUser.uid,
                privateKey: privateKey,
            });

            if (result.data.error) {
                toast.error(result.data.error);
                setDeletionResult({
                    success: false,
                    error: result.data.error,
                    summary: result.data.summary,
                });
            } else {
                toast.success('User deleted successfully');
                setDeletionResult({
                    success: true,
                    summary: result.data.summary,
                });
                // Reset form
                setSelectedUser(null);
                setSearchInput('');
                setPrivateKey('');
                setConfirmText('');
                setShowConfirmDialog(false);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user: ' + error.message);
            setDeletionResult({
                success: false,
                error: error.message,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClearResults = () => {
        setDeletionResult(null);
    };

    return (
        <div className="page-size flex flex-col gap-4 justify-start p-4">
            <div className="flex flex-col gap-2">
                <h1 className="font-bold text-3xl">Delete User</h1>
                <div className="alert alert-warning">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="stroke-current shrink-0 h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                    <span>
                        Warning: This action is permanent and cannot be undone. All user data,
                        including fighter profiles, event registrations, orders, and storage
                        files will be permanently deleted.
                    </span>
                </div>
            </div>

            {/* User Search Section */}
            <div className="card bg-base-100 border">
                <div className="card-body">
                    <h2 className="card-title">Step 1: Search User</h2>
                    <div className="flex flex-col md:flex-row gap-2">
                        <input
                            type="text"
                            placeholder="Enter user email or UID"
                            className="input input-bordered flex-1"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchUser();
                                }
                            }}
                        />
                        <button
                            className="btn btn-primary"
                            onClick={handleSearchUser}
                        >
                            Search
                        </button>
                    </div>

                    {selectedUser && (
                        <div className="mt-4 p-4 bg-base-200 rounded-lg">
                            <h3 className="font-bold mb-2">Selected User:</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div>
                                    <span className="font-semibold">UID:</span> {selectedUser.uid}
                                </div>
                                <div>
                                    <span className="font-semibold">Email:</span>{' '}
                                    {selectedUser.email || 'N/A'}
                                </div>
                                <div>
                                    <span className="font-semibold">Display Name:</span>{' '}
                                    {selectedUser.displayName || 'N/A'}
                                </div>
                                <div>
                                    <span className="font-semibold">Role:</span>{' '}
                                    {selectedUser.role || 'N/A'}
                                </div>
                                <div>
                                    <span className="font-semibold">Phone:</span>{' '}
                                    {selectedUser.phoneNumber || 'N/A'}
                                </div>
                                <div>
                                    <span className="font-semibold">Auth Method:</span>{' '}
                                    {selectedUser.authMethod || 'N/A'}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Private Key Section */}
            <div className="card bg-base-100 border">
                <div className="card-body">
                    <h2 className="card-title">Step 2: Enter Deletion Private Key</h2>
                    <input
                        type="password"
                        placeholder="Deletion Private Key"
                        className="input input-bordered"
                        value={privateKey}
                        onChange={(e) => setPrivateKey(e.target.value)}
                    />
                    <p className="text-sm text-gray-500">
                        This key is required for additional security. Contact the system
                        administrator if you don't have it.
                    </p>
                </div>
            </div>

            {/* Delete Button */}
            <div className="card bg-base-100 border">
                <div className="card-body">
                    <h2 className="card-title">Step 3: Confirm Deletion</h2>
                    <button
                        className="btn btn-error"
                        onClick={handleOpenConfirmDialog}
                        disabled={!selectedUser || !privateKey.trim() || isDeleting}
                    >
                        {isDeleting ? (
                            <>
                                <span className="loading loading-spinner"></span>
                                Deleting...
                            </>
                        ) : (
                            'Delete User'
                        )}
                    </button>
                </div>
            </div>

            {/* Deletion Result */}
            {deletionResult && (
                <div className="card bg-base-100 border">
                    <div className="card-body">
                        <h2 className="card-title">Deletion Result</h2>
                        {deletionResult.success ? (
                            <div className="alert alert-success">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="stroke-current shrink-0 h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span>User deleted successfully!</span>
                            </div>
                        ) : (
                            <div className="alert alert-error">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="stroke-current shrink-0 h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span>Error: {deletionResult.error}</span>
                            </div>
                        )}

                        {deletionResult.summary && (
                            <div className="mt-4">
                                <h3 className="font-bold mb-2">Deletion Summary:</h3>
                                <div className="bg-base-200 p-4 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <div>
                                            <span className="font-semibold">User ID:</span>{' '}
                                            {deletionResult.summary.userId}
                                        </div>
                                        <div>
                                            <span className="font-semibold">User Document:</span>{' '}
                                            {deletionResult.summary.deletedDocuments.user
                                                ? '✓ Deleted'
                                                : '✗ Not deleted'}
                                        </div>
                                        <div>
                                            <span className="font-semibold">Fighter Document:</span>{' '}
                                            {deletionResult.summary.deletedDocuments.fighter
                                                ? '✓ Deleted'
                                                : '✗ Not deleted'}
                                        </div>
                                        <div>
                                            <span className="font-semibold">Event Fighters:</span>{' '}
                                            {deletionResult.summary.deletedDocuments.eventFighters}{' '}
                                            deleted
                                        </div>
                                        <div>
                                            <span className="font-semibold">Fighter Requests:</span>{' '}
                                            {deletionResult.summary.deletedDocuments.fighterRequests}{' '}
                                            deleted
                                        </div>
                                        <div>
                                            <span className="font-semibold">Orders:</span>{' '}
                                            {deletionResult.summary.deletedDocuments.orders} deleted
                                        </div>
                                        <div>
                                            <span className="font-semibold">Live Stream Tickets:</span>{' '}
                                            {deletionResult.summary.deletedDocuments.liveStreamTickets}{' '}
                                            deleted
                                        </div>
                                        <div>
                                            <span className="font-semibold">Storage Files:</span>{' '}
                                            {deletionResult.summary.deletedStorageFiles} deleted
                                        </div>
                                        <div>
                                            <span className="font-semibold">Auth User:</span>{' '}
                                            {deletionResult.summary.deletedAuthUser
                                                ? '✓ Deleted'
                                                : '✗ Not deleted'}
                                        </div>
                                    </div>

                                    {deletionResult.summary.errors &&
                                        deletionResult.summary.errors.length > 0 && (
                                            <div className="mt-4">
                                                <h4 className="font-bold text-error">Errors:</h4>
                                                <ul className="list-disc list-inside">
                                                    {deletionResult.summary.errors.map(
                                                        (error, index) => (
                                                            <li key={index} className="text-error">
                                                                {error}
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}

                        <button className="btn btn-ghost mt-4" onClick={handleClearResults}>
                            Clear Results
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg text-error">Confirm User Deletion</h3>
                        <div className="py-4">
                            <div className="alert alert-error mb-4">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="stroke-current shrink-0 h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span>This action cannot be undone!</span>
                            </div>

                            <p className="mb-4">
                                You are about to permanently delete the following user:
                            </p>
                            <div className="bg-base-200 p-4 rounded-lg mb-4">
                                <div>
                                    <span className="font-semibold">Email:</span>{' '}
                                    {selectedUser?.email}
                                </div>
                                <div>
                                    <span className="font-semibold">UID:</span> {selectedUser?.uid}
                                </div>
                                <div>
                                    <span className="font-semibold">Role:</span>{' '}
                                    {selectedUser?.role}
                                </div>
                            </div>

                            <p className="mb-4 font-bold">The following data will be deleted:</p>
                            <ul className="list-disc list-inside mb-4">
                                <li>User account and profile</li>
                                <li>Fighter profile (if exists)</li>
                                <li>All event registrations</li>
                                <li>All fighter requests</li>
                                <li>All orders and tickets</li>
                                <li>All storage files (photos, receipts)</li>
                                <li>Firebase Authentication account</li>
                            </ul>

                            <p className="mb-2">
                                Type <span className="font-bold text-error">DELETE</span> to
                                confirm:
                            </p>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="Type DELETE"
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => {
                                    setShowConfirmDialog(false);
                                    setConfirmText('');
                                }}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleDeleteUser}
                                disabled={confirmText !== 'DELETE' || isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="loading loading-spinner"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    'Confirm Delete'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
